import React from 'react';
import ListView from '../../components/ListView';
import CalendarView from '../../components/CalendarView';
import { DayView } from '../../components/DayView';
import { TimelineView } from '../../components/TimelineView';
import WeekView from '../../components/WeekView';
import MonthView from '../../components/MonthView';
import YearView from '../../components/YearView';
import { Todo, TodoCreate, TaskStatus, User, TeamMember } from '../../types';
import type { ViewType } from '../../components/ViewTabs';

interface AppViewsProps {
  activeView: ViewType;
  todos: Todo[];
  allTodos: Todo[];
  allTags: string[];
  selectedTeamId: string | null;
  teamMembers: TeamMember[];
  isPersonalMode: boolean;
  currentUser: User | undefined;
  searchQuery: string;
  showFilters: boolean;
  currentFilters: {
    statusFilter: TaskStatus[];
    horizonFilter: string[];
    tagsFilter: string[];
    taskTypeFilter: 'all' | 'parent_only' | 'child_only';
    setStatusFilter: (filter: TaskStatus[]) => void;
    setHorizonFilter: (filter: string[]) => void;
    setTagsFilter: (filter: string[]) => void;
    setTaskTypeFilter: (filter: 'all' | 'parent_only' | 'child_only') => void;
  };
  
  // Event handlers
  onTodoClick: (todo: Todo) => void;
  onTodoClickWithContext: (todo: Todo, modalContext?: { previousModal: string; previousPhase?: string; previousState?: Record<string, unknown> }) => void;
  onTodoStatusChange: (todoId: string, status: TaskStatus) => void;
  onTodoDateChange: (todoId: string, date: string | null, dateType: 'deadline') => void;
  onAddChild: (parentId: string) => void;
  onMakeChild: (childId: string, parentId: string) => void;
  onStartPomodoro: (todo: Todo) => void;
  onDeleteTodo: (todoId: string) => void;
  onUpdateTodo: (data: TodoCreate) => void;
  onCancelTodoModal: () => void;
  onCloneTodo: (todoId: string) => Promise<void>;
  onSearchChange: (query: string) => void;
  onToggleFilters: () => void;
  onStatusToggle: (status: TaskStatus) => void;
  onHorizonToggle: (horizon: string) => void;
  onTagToggle: (tag: string) => void;
  onTaskTypeChange: (type: 'all' | 'parent_only' | 'child_only') => void;
  onClearFilters: () => void;
  onCalendarCreateTodo: (dueDate: string) => void;
  onAddTodo: () => void;
  onAddTodayTask: () => void;
  onAddWeekTask: () => void;
  onStartWeeklyReview: () => void;
  onCreateTodo: (todo: TodoCreate) => Promise<Todo>;
  onUpdateTodoAsync: (todoId: string, updates: Partial<TodoCreate>) => Promise<void>;
  updateTodo: (params: { id: string; data: Partial<Todo> }) => void;
  onStartMonthlyReview: () => void;
  onStartYearlyReview: () => void;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', message: string) => void;
}

export function AppViews({
  activeView,
  todos,
  allTodos,
  allTags,
  selectedTeamId,
  teamMembers,
  isPersonalMode,
  currentUser,
  searchQuery,
  showFilters,
  currentFilters,
  onTodoClick,
  onTodoClickWithContext,
  onTodoStatusChange,
  onTodoDateChange,
  onAddChild,
  onMakeChild,
  onStartPomodoro,
  onDeleteTodo,
  onUpdateTodo,
  onCancelTodoModal,
  onCloneTodo,
  onSearchChange,
  onToggleFilters,
  onStatusToggle,
  onHorizonToggle,
  onTagToggle,
  onTaskTypeChange,
  onClearFilters,
  onCalendarCreateTodo,
  onAddTodo,
  onAddTodayTask,
  onAddWeekTask,
  onStartWeeklyReview,
  onCreateTodo,
  onUpdateTodoAsync,
  updateTodo,
  onStartMonthlyReview,
  onStartYearlyReview
  // showNotification is passed but not currently used in views
}: AppViewsProps) {
  switch (activeView) {
    case 'list':
      return (
        <ListView
          todos={todos}
          onTodoClick={onTodoClick}
          onTodoStatusChange={onTodoStatusChange}
          onAddChild={onAddChild}
          onMakeChild={onMakeChild}
          onStartPomodoro={onStartPomodoro}
          allTodos={allTodos}
          teamMembers={teamMembers}
          isPersonalMode={isPersonalMode}
        />
      );
      
    case 'calendar':
      return (
        <CalendarView
          todos={todos}
          onTodoClick={onTodoClick}
          onTodoStatusChange={onTodoStatusChange}
          onTodoDateChange={onTodoDateChange}
          onCreateTodo={onCalendarCreateTodo}
        />
      );
      
    case 'day':
      return (
        <DayView 
          todos={todos} 
          onTaskClick={onTodoClick}
          onTodoStatusChange={onTodoStatusChange}
          selectedTeamId={selectedTeamId}
          teamMembers={teamMembers}
          isPersonalMode={isPersonalMode}
          currentUser={currentUser}
          allTags={allTags}
          onUpdateTodo={onUpdateTodo}
          onCancelTodoModal={onCancelTodoModal}
          onDeleteTodo={onDeleteTodo}
          onCloneTodo={onCloneTodo}
          onAddTodayTask={onAddTodayTask}
          onStartPomodoro={onStartPomodoro}
          onMakeChild={onMakeChild}
        />
      );
      
    case 'timeline':
      return (
        <TimelineView 
          todos={todos}
          onTaskClick={onTodoClick}
        />
      );
      
    case 'week':
      return (
        <WeekView
          todos={todos}
          selectedTeamId={selectedTeamId}
          onTodoClick={onTodoClickWithContext}
          onTodoStatusChange={(todoId: string, statusOrData: TaskStatus | Record<string, unknown>) => {
            if (typeof statusOrData === 'string') {
              onTodoStatusChange(todoId, statusOrData as TaskStatus);
            } else {
              updateTodo({ id: todoId, data: statusOrData });
            }
          }}
          onAddTodo={onAddTodo}
          onAddTodayTask={onAddTodayTask}
          onAddWeekTask={onAddWeekTask}
          onStartWeeklyReview={onStartWeeklyReview}
          onAddChild={onAddChild}
          onMakeChild={onMakeChild}
          onStartPomodoro={onStartPomodoro}
          onDelete={onDeleteTodo}
          teamMembers={teamMembers}
          isPersonalMode={isPersonalMode}
          statusFilter={currentFilters.statusFilter}
          onStatusFilterChange={(statuses: TaskStatus[]) => {
            currentFilters.setStatusFilter(statuses);
          }}
          searchQuery={searchQuery}
          onSearchChange={onSearchChange}
          showFilters={showFilters}
          onToggleFilters={onToggleFilters}
          onStatusToggle={onStatusToggle}
          horizonFilter={currentFilters.horizonFilter}
          onHorizonToggle={onHorizonToggle}
          tagsFilter={currentFilters.tagsFilter}
          onTagToggle={onTagToggle}
          taskTypeFilter={currentFilters.taskTypeFilter}
          onTaskTypeChange={onTaskTypeChange}
          allTags={allTags}
          onClearFilters={onClearFilters}
          allTodos={allTodos}
          onCreateTodo={onCreateTodo}
          onUpdateTodo={onUpdateTodoAsync}
        />
      );
      
    case 'month':
      return (
        <MonthView
          selectedTeamId={selectedTeamId}
          onTodoClick={onTodoClickWithContext}
          onTodoStatusChange={(todoId: string, statusOrData: TaskStatus | Record<string, unknown>) => {
            if (typeof statusOrData === 'string') {
              onTodoStatusChange(todoId, statusOrData as TaskStatus);
            } else {
              updateTodo({ id: todoId, data: statusOrData });
            }
          }}
          onAddTodo={() => {
            onAddTodo();
          }}
          onAddChild={onAddChild}
          onMakeChild={onMakeChild}
          onStartPomodoro={onStartPomodoro}
          onDelete={onDeleteTodo}
          teamMembers={teamMembers}
          isPersonalMode={isPersonalMode}
          allTodos={allTodos}
          onStartMonthlyReview={onStartMonthlyReview}
          onCreateTodo={onCreateTodo}
          onUpdateTodo={onUpdateTodoAsync}
        />
      );
      
    case 'year':
      return (
        <YearView
          selectedTeamId={selectedTeamId}
          onAddTodo={onAddTodo}
          onStartYearlyReview={onStartYearlyReview}
        />
      );
      
    default:
      return null;
  }
}